import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import type { ControlViolation, InventoryResource } from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import {
  ControlIndicator,
  controlKeysPriorities,
} from '@/components/controls/controlIndicator';
import { ControlSummary } from '@/components/controls/controlSummary';
import { Loader } from '@/components/layout/loading';
import OptionMenu from '@/components/option-menu/option-menu';
import { Icon } from '@/components/shared/icon';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { ControlsSeverity } from '@/data/inventory/data';

export const ViolationsDetails = ({
  resourceId,
  onEntityChange,
  entity,
}: {
  resourceId: string;
  onEntityChange?: () => Promise<void>;
  entity: InventoryResource;
}) => {
  const { isAuthorized, isAuthorizedResource } = useRoles();
  const dal = useDAL();
  const router = useRouter();
  const { data: body, mutate: mutateViolations } =
    dal.inventory.violations.list(resourceId);
  if (!body?.controlViolations) {
    return <Loader />;
  }

  const groupedResponse = body.controlViolations.reduce<
    Record<string, ControlViolation[]>
  >((acc, violation) => {
    const key = violation.muted
      ? 'muted'
      : violation.control.severity.toLowerCase();

    acc[key] = acc[key] || [];
    acc[key].push(violation);

    return acc;
  }, {});

  const toggleViolationMute = (violation: ControlViolation) => {
    void dal.inventory.violations
      .update(violation.resourceId, {
        id: violation.id,
        resourceId: violation.resourceId,
        muted: !violation.muted,
      })
      .then(() => {
        void mutateViolations();
        void onEntityChange?.();
      });
  };

  const filteredPriorities = controlKeysPriorities.filter(
    (level) => groupedResponse[level]
  );

  return (
    <Stack width={'100%'} className='mt-[12px]' gap='24px'>
      <Divider sx={{ margin: '0 40px', borderStyle: 'dashed' }} />
      <Typography paddingX='40px' variant='subtitle1'>
        Violated controls:
      </Typography>
      <Box>
        {filteredPriorities.map((level, levelIndex) => {
          const groupedControls =
            groupedResponse[level].reduce<Record<string, ControlViolation[]>>(
              (acc, violation) => {
                const priority = violation.control.severity.toLowerCase();
                acc[priority] = acc[priority] || [];
                acc[priority].push(violation);
                return acc;
              },
              {}
            ) || {};

          return (
            <Stack spacing='12px' key={level}>
              <Accordion>
                <AccordionSummary
                  sx={{ paddingX: '40px' }}
                  data-testid='backup-status-content'
                >
                  <Stack
                    className='w-full'
                    direction={'row'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                  >
                    <Typography variant='h4'>
                      {`${(groupedResponse[level] || []).length} ${level === 'muted' ? ' muted violations' : `${level}-severity control violations`}`}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ paddingRight: '24px', paddingLeft: '64px' }}
                >
                  {(level === 'muted'
                    ? controlKeysPriorities.filter((x) => x !== 'muted')
                    : [level]
                  ).map((internalLevel, index) => {
                    if (!groupedControls[internalLevel]?.length) {
                      return null;
                    }

                    return (
                      <Box key={internalLevel}>
                        {level === 'muted' && (
                          <Typography
                            variant='subtitle1'
                            className='uppercase'
                            sx={{
                              marginBottom: '16px',
                              marginTop: index === 0 ? '4px' : '24px',
                            }}
                          >{`${internalLevel}-severity controls`}</Typography>
                        )}
                        {groupedControls[internalLevel].map((violation) => {
                          return (
                            <Stack
                              key={violation.control.name}
                              direction={'row'}
                              justifyContent={'space-between'}
                              alignItems={'center'}
                              className='py-[6px]'
                            >
                              <Stack direction='row' alignItems='center'>
                                <ControlIndicator
                                  backgroundColor={
                                    ControlsSeverity[level].color
                                  }
                                />
                                <Box component={'span'} className='ml-[16px]'>
                                  {violation.control.name}
                                </Box>
                              </Stack>
                              <Stack direction={'row'} alignItems={'center'}>
                                <OptionMenu
                                  data-testid='violation-control-options'
                                  options={[
                                    {
                                      id: 'mute-violation',
                                      text:
                                        level === 'muted' ? 'Unmute' : 'Mute',
                                      menuItemProps: {
                                        disabled: !isAuthorizedResource(
                                          'update:control_violations',
                                          entity
                                        ),
                                        onClick: () => {
                                          toggleViolationMute(violation);
                                        },
                                      },
                                    },
                                    {
                                      id: 'violation-control-settings',
                                      text: 'Control settings',
                                      menuItemProps: {
                                        disabled:
                                          !isAuthorized('read:controls'),
                                        onClick: () => {
                                          router.push('/settings/controls');
                                        },
                                      },
                                    },
                                  ]}
                                />
                                <Tooltip
                                  disableInteractive={false}
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        minWidth: '400px',
                                      },
                                    },
                                  }}
                                  title={
                                    <ControlSummary
                                      control={violation.control}
                                    />
                                  }
                                >
                                  <Icon iconClass='material-symbols-info-outline' />
                                </Tooltip>
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
              {levelIndex !== filteredPriorities.length - 1 ? (
                <Divider className='mb-[12px]' />
              ) : null}
            </Stack>
          );
        })}
      </Box>
    </Stack>
  );
};
